import { useCallback, useEffect, useState } from "react";
import { BadgeIndianRupee, Loader2, Plus, Printer, Users, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { LeavePolicies, LeaveQueue } from "@/components/hr/LeavePanels";
import { LeaveCalendar } from "@/components/hr/LeaveCalendar";
import { stopRow, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money, shortDate } from "@/lib/format";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK_STAFF = { name: "", email: "", job_title: "", department: "programmes",
  contract_type: "permanent", country: "IN", currency: "INR", gross_monthly: 0,
  employer_cost_pct: 13, start_date: "", manager: "", allocations: [], leave_balance_days: 18 };

const thisPeriod = () => new Date().toISOString().slice(0, 7);

export default function HrPayroll() {
  const { can } = useAuth();
  const [staff, setStaff] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const [funds, setFunds] = useState([]);
  const [dialog, setDialog] = useState("");
  const [form, setForm] = useState(BLANK_STAFF);
  const [splits, setSplits] = useState([{ fund_id: "", programme_code: "", percent: 100 }]);
  const [editing, setEditing] = useState(null);
  const [period, setPeriod] = useState(thisPeriod());
  const [slip, setSlip] = useState(null);
  const detail = useRowDetail("staff", () => load());
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [s, p, f] = await Promise.all([
      api.get("/hr/staff"), api.get("/hr/payroll"), api.get("/charity/funds"),
    ]);
    setStaff(s.data);
    setPayroll(p.data);
    setFunds(Array.isArray(f.data) ? f.data : f.data.items || []);
  }, []);

  useEffect(() => { load().catch(() => setStaff(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      setDialog("");
      await load();
      return res;
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const openPayslip = async (staffRef, runPeriod) => {
    try {
      const res = await api.get(`/hr/payroll/${runPeriod}/payslip/${staffRef}`);
      setSlip(res.data);
      setDialog("slip");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!staff) return <Loading label="Loading staff and payroll" />;
  const splitTotal = splits.reduce((a, s) => a + Number(s.percent || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-028 · HR-001 / HR-002 / HR-003"
        title="Who works here, and which fund actually pays for them"
        description="Staff records with contract, manager and leave, and a monthly payroll run that splits every person's gross and employer cost across the funds and programmes that carry it. Preparing the run and posting it are two different people, the posting is refused if a fund is over budget, and each person gets a printable payslip showing their own fund split."
        actions={can("hr", "write") && (
          <Dialog open={dialog === "staff"} onOpenChange={(o) => setDialog(o ? "staff" : "")}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="new-staff-button"
                      className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader><DialogTitle>New staff record</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Name">
                  <Input data-testid="staff-name-input" className="bg-muted/40" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </F>
                <F label="Work email">
                  <Input data-testid="staff-email-input" className="bg-muted/40" value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </F>
                <F label="Job title">
                  <Input data-testid="staff-title-input" className="bg-muted/40" value={form.job_title}
                         onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                </F>
                <F label="Department">
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="staff-department-select" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["programmes", "finance", "fundraising", "operations", "governance"].map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Gross monthly">
                  <Input type="number" data-testid="staff-gross-input" className="bg-muted/40"
                         value={form.gross_monthly}
                         onChange={(e) => setForm({ ...form, gross_monthly: Number(e.target.value) })} />
                </F>
                <F label="Employer cost %">
                  <Input type="number" data-testid="staff-employer-input" className="bg-muted/40"
                         value={form.employer_cost_pct}
                         onChange={(e) => setForm({ ...form, employer_cost_pct: Number(e.target.value) })} />
                </F>
                <F label="Start date">
                  <Input type="date" data-testid="staff-start-input" className="bg-muted/40"
                         value={form.start_date}
                         onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </F>
                <F label="Manager">
                  <Input data-testid="staff-manager-input" className="bg-muted/40" value={form.manager}
                         onChange={(e) => setForm({ ...form, manager: e.target.value })} />
                </F>
              </div>
              <Button data-testid="staff-save-button" disabled={busy === "staff"}
                      onClick={() => act("staff", () => api.post("/hr/staff", form),
                                        "Staff record created — now set the fund split")}
                      className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "staff" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Save the record
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-headcount" label="Headcount" value={staff.counters.headcount} accent="sky"
             icon={Users} sub={`${staff.counters.leavers} leavers`} />
        <Kpi testId="kpi-monthly-cost" label="Monthly staff cost" accent="violet" icon={Wallet}
             value={money(staff.counters.monthly_cost, "INR", true)} />
        <Kpi testId="kpi-unallocated" label="Without a fund split" value={staff.counters.unallocated}
             accent={staff.counters.unallocated ? "amber" : "emerald"} />
        <Kpi testId="kpi-runs" label="Payroll runs" value={payroll.counters.runs} accent="teal"
             sub={`${payroll.counters.posted} posted`} />
        <Kpi testId="kpi-ytd" label="Posted this year" accent="emerald" icon={BadgeIndianRupee}
             value={money(payroll.counters.ytd_cost, "INR", true)} />
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll runs</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="leave-calendar" data-testid="tab-leave-calendar">
            Who is away
          </TabsTrigger>
          <TabsTrigger value="leave-policy" data-testid="tab-leave-policy">Leave policy</TabsTrigger>
        </TabsList>

        <TabsContent value="leave" className="mt-4">
          <LeaveQueue period={period} />
        </TabsContent>

        <TabsContent value="leave-calendar" className="mt-4">
          <LeaveCalendar initialMonth={period} />
        </TabsContent>

        <TabsContent value="leave-policy" className="mt-4">
          <LeavePolicies />
        </TabsContent>

        <TabsContent value="staff" className="mt-4">
          <Panel testId="panel-staff" kicker="HR-001" title="Staff register">
            <Table testId="staff-table" head={<>
              <Th>Reference</Th><Th>Name</Th><Th>Role</Th><Th>Gross</Th><Th>Total cost</Th>
              <Th>Fund split</Th><Th>Leave</Th><Th /></>}>
              {staff.items.map((s) => (
                <tr key={s.staff_ref} className="cursor-pointer hover:bg-muted/40"
                    onClick={() => detail.show(s, s.staff_ref, s.name, s.job_title)}
                    data-testid={`staff-${s.staff_ref}`}>
                  <Td className="font-mono text-[11px]">{s.staff_ref}</Td>
                  <Td>
                    <div className="font-semibold text-foreground">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground">{s.email || "—"}</div>
                  </Td>
                  <Td>
                    <div>{s.job_title}</div>
                    <div className="text-[11px] capitalize text-muted-foreground">{s.department}</div>
                  </Td>
                  <Td className="tabular-nums">{money(s.gross_monthly, s.currency)}</Td>
                  <Td className="tabular-nums">{money(s.total_monthly_cost, s.currency)}</Td>
                  <Td>
                    {s.allocations.length === 0
                      ? <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                          not allocated
                        </Chip>
                      : (
                        <div className="flex flex-wrap gap-1">
                          {s.allocations.map((a, i) => (
                            <Chip key={i} className="border-border bg-muted text-muted-foreground">
                              {a.fund_code} {a.percent}%{a.programme_code ? ` · ${a.programme_code}` : ""}
                            </Chip>
                          ))}
                        </div>
                      )}
                  </Td>
                  <Td className="tabular-nums">{s.leave_balance_days ?? "—"} d</Td>
                  <Td onClick={stopRow}>
                    {can("hr", "write") && (
                      <Button size="sm" variant="outline" data-testid={`allocate-${s.staff_ref}`}
                              onClick={() => { setEditing(s);
                                setSplits(s.allocations.length
                                  ? s.allocations.map((a) => ({ fund_id: a.fund_id,
                                      programme_code: a.programme_code || "", percent: a.percent }))
                                  : [{ fund_id: "", programme_code: "", percent: 100 }]);
                                setDialog("split"); }}
                              className="h-7 rounded-full border-border text-[11px]">
                        Fund split
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
              {staff.items.length === 0 && <Empty colSpan={8} label="No staff records yet" />}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="payroll" className="mt-4">
          <Panel testId="panel-payroll" kicker="HR-002 · payroll cost allocation"
                 title="Monthly runs and where the cost landed"
                 actions={can("hr", "write") && (
                   <div className="flex items-center gap-2">
                     <Input type="month" data-testid="payroll-period-input" value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className="h-8 w-36 bg-muted/40 text-xs" />
                     <Button size="sm" data-testid="prepare-payroll-button" disabled={busy === "prep"}
                             onClick={() => act("prep", () => api.post("/hr/payroll", { period }),
                                               (res) => `Run prepared for ${res.data.headcount} people · ${money(res.data.total_cost, "INR", true)}`)}
                             className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                       Prepare run
                     </Button>
                   </div>
                 )}>
            <div className="space-y-4 p-4 pt-0">
              {payroll.items.length === 0 && (
                <div className="py-8 text-center text-xs text-muted-foreground" data-testid="empty-payroll">
                  No payroll has been prepared yet.
                </div>
              )}
              {payroll.items.map((run) => (
                <div key={run.period} className="rounded-xl border border-border bg-muted/20 p-3"
                     data-testid={`run-${run.period}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-foreground">{run.period}</div>
                      <div className="text-[11.5px] text-muted-foreground">
                        {run.headcount} people · gross {money(run.total_gross, "INR", true)} ·
                        total cost {money(run.total_cost, "INR", true)} · prepared by {run.prepared_by}
                        {run.posted_by ? ` · posted by ${run.posted_by} ${dateTime(run.posted_at)}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip className={run.status === "posted"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}
                            testId={`run-status-${run.period}`}>{run.status}</Chip>
                      {run.status === "prepared" && can("hr", "approve") && (
                        <Button size="sm" data-testid={`post-${run.period}`} disabled={busy === run.period}
                                onClick={() => act(run.period,
                                  () => api.post(`/hr/payroll/${run.period}/post`),
                                  "Payroll posted — the cost now sits on each fund")}
                                className="h-7 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                          Post to the ledger
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {run.by_fund.map((f) => (
                      <Chip key={f.fund_code} className="border-primary/30 bg-primary/10 text-primary">
                        {f.fund_code} · {money(f.amount, "INR", true)}
                      </Chip>
                    ))}
                  </div>

                  <div className="mt-3">
                    <Table testId={`run-lines-${run.period}`} head={<>
                      <Th>Staff</Th><Th>Gross</Th><Th>Employer cost</Th><Th>Total</Th>
                      <Th>Allocated to</Th><Th /></>}>
                      {run.lines.map((l) => (
                        <tr key={l.staff_ref} className="cursor-pointer hover:bg-muted/40"
                            onClick={() => detail.show(l, l.staff_ref, l.name,
                                                       `${run.period} payroll line`)}
                            data-testid={`run-line-${run.period}-${l.staff_ref}`}>
                          <Td>
                            <div className="font-semibold text-foreground">{l.name}</div>
                            <div className="text-[11px] text-muted-foreground">{l.job_title}</div>
                          </Td>
                          <Td className="tabular-nums">{money(l.gross, "INR", true)}</Td>
                          <Td className="tabular-nums">{money(l.employer_cost, "INR", true)}</Td>
                          <Td className="tabular-nums">{money(l.total_cost, "INR", true)}</Td>
                          <Td>
                            <div className="flex flex-wrap gap-1">
                              {l.splits.map((s, i) => (
                                <Chip key={i} className="border-border bg-muted text-muted-foreground">
                                  {s.fund_code} {s.percent}% · {money(s.amount, "INR", true)}
                                </Chip>
                              ))}
                            </div>
                          </Td>
                          <Td onClick={stopRow}>
                            <Button size="sm" variant="outline"
                                    data-testid={`payslip-${run.period}-${l.staff_ref}`}
                                    onClick={() => openPayslip(l.staff_ref, run.period)}
                                    className="h-7 rounded-full border-border text-[11px]">
                              <Printer className="mr-1 h-3 w-3" /> Payslip
                            </Button>
                          </Td>
                        </tr>
                      ))}
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={dialog === "split"} onOpenChange={(o) => setDialog(o ? "split" : "")}>
        <DialogContent className="max-w-lg border-border bg-card">
          <DialogHeader>
            <DialogTitle>Fund split — {editing?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Payroll debits each fund in these proportions. The percentages must add up to 100.
          </p>
          <div className="space-y-2">
            {splits.map((s, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`split-row-${i}`}>
                <div className="col-span-6"><F label="Fund">
                  <Select value={s.fund_id}
                          onValueChange={(v) => setSplits(splits.map((x, j) => j === i ? { ...x, fund_id: v } : x))}>
                    <SelectTrigger data-testid={`split-fund-${i}`} className="bg-muted/40">
                      <SelectValue placeholder="Choose a fund" /></SelectTrigger>
                    <SelectContent>
                      {funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.code} · {f.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F></div>
                <div className="col-span-3"><F label="Programme">
                  <Input data-testid={`split-programme-${i}`} className="bg-muted/40"
                         value={s.programme_code}
                         onChange={(e) => setSplits(splits.map((x, j) => j === i ? { ...x, programme_code: e.target.value } : x))} />
                </F></div>
                <div className="col-span-2"><F label="%">
                  <Input type="number" data-testid={`split-percent-${i}`} className="bg-muted/40"
                         value={s.percent}
                         onChange={(e) => setSplits(splits.map((x, j) => j === i ? { ...x, percent: Number(e.target.value) } : x))} />
                </F></div>
                <Button size="sm" variant="outline" data-testid={`split-remove-${i}`}
                        onClick={() => setSplits(splits.filter((_, j) => j !== i))}
                        className="col-span-1 h-9 rounded-full border-border">×</Button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" data-testid="split-add-button"
                    onClick={() => setSplits([...splits, { fund_id: "", programme_code: "", percent: 0 }])}
                    className="h-8 rounded-full border-border text-[11px]">
              <Plus className="mr-1 h-3 w-3" /> Add a fund
            </Button>
            <span className={splitTotal === 100 ? "text-[11.5px] text-emerald-600 dark:text-emerald-400"
              : "text-[11.5px] text-amber-600 dark:text-amber-400"} data-testid="split-total">
              {splitTotal}% allocated
            </span>
          </div>
          <Button data-testid="split-save-button" disabled={busy === "split" || splitTotal !== 100}
                  onClick={() => act("split",
                    () => api.put(`/hr/staff/${editing.id}/allocations`, { allocations: splits }),
                    "Fund split saved")}
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "split" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Save the split
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "slip"} onOpenChange={(o) => setDialog(o ? "slip" : "")}>
        <DialogContent className="max-w-md border-border bg-card print:shadow-none">
          <DialogHeader><DialogTitle>Payslip</DialogTitle></DialogHeader>
          {slip && (
            <div className="space-y-3 text-sm" id="payslip" data-testid="payslip-body">
              <div>
                <div className="text-base font-semibold text-foreground">{slip.organization.name}</div>
                <div className="text-[11.5px] text-muted-foreground">
                  Payslip for {slip.period} · {slip.status}
                  {slip.posted_at ? ` · posted ${shortDate(slip.posted_at)}` : ""}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs">
                <div className="font-semibold text-foreground">{slip.staff.name}</div>
                <div className="text-muted-foreground">
                  {slip.staff.job_title} · {slip.staff.staff_ref} · {slip.staff.department}
                </div>
                <div className="text-muted-foreground">{slip.staff.email}</div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Gross pay</span>
                  <span className="tabular-nums">{money(slip.gross, slip.staff.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Employer contributions</span>
                  <span className="tabular-nums">{money(slip.employer_cost, slip.staff.currency)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-semibold">
                  <span>Total cost to the charity</span>
                  <span className="tabular-nums">{money(slip.total_cost, slip.staff.currency)}</span></div>
              </div>
              <div>
                <div className="kicker mb-1">Funded by</div>
                <div className="space-y-1 text-xs">
                  {slip.splits.map((s, i) => (
                    <div key={i} className="flex justify-between" data-testid={`slip-split-${i}`}>
                      <span className="text-muted-foreground">
                        {s.fund_code}{s.programme_code ? ` · ${s.programme_code}` : ""} · {s.percent}%
                      </span>
                      <span className="tabular-nums">{money(s.amount, slip.staff.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                Leave balance {slip.staff.leave_balance_days ?? "—"} days. This payslip is generated
                from the payroll run and the fund allocation held on the staff record.
              </div>
              <Button size="sm" data-testid="payslip-print-button" onClick={() => window.print()}
                      className="h-8 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Printer className="mr-1 h-3.5 w-3.5" /> Print
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {detail.sheet}
    </div>
  );
}
